/**
 * Componente: StickyCartBar
 * -----------------------------------------------------------------------------
 * - Gestiona la visibilidad de una barra de carrito fija en función de la posición de desplazamiento y
 * los elementos observados (actualmente: pie de página y bloques relacionados con la información del producto).
 * - Activa automáticamente la clase «is-sticky» en función de la visibilidad.
 *
 * Uso:
 * - Se utiliza normalmente en páginas de productos, pero es flexible para otros casos de uso.
 * - Ejemplo de estructura de página (el encabezado y el pie de página son opcionales):
 *
 * <header> Contenido de la cabecera </header>
 * <div data-js-product-box="">
 *   <!-- Product info content -->
 *   <button js-add-to-cart="">Añadir a la cesta</button>
 * </div>
 * <sticky-cart-bar class="sticky-cart-bar">
 *   
 * </sticky-cart-bar>
 * <footer data-js-footer=""> Contenido del pie de página </footer>
 *
 * Requirements:
 * - Either `data-js-product-box` or `js-add-to-cart` must be present for the
 * sticky logic to function properly.
 */
;(() => {
  if ('customElements' in window && customElements.get('sticky-cart-bar')) {
    return
  }
  class StickyCartBar extends HTMLElement {
    constructor() {
      super()

      /** @type {HTMLElement | null} The footer element to observe. */
      this.footer = null

      /** @type {HTMLElement | null} The reference element to observe for sticky logic. */
      this.referenceElement = null

      /** @type {HTMLElement[]} Array of all the elements to observe. */
      this.elementsToObserve = []

      /** @type {IntersectionObserver | null} IntersectionObserver instance. */
      this.observer = null

      this.handleIntersection = this.handleIntersection.bind(this)
    }

    /**
     * Called when the component is added to the DOM.
     */
    connectedCallback() {
      this.setDefaultState()
      this.setupObservers()
    }

    /**
     * Called when the component is removed from the DOM.
     */
    disconnectedCallback() {
      this.disconnectObservers()
    }

    /**
     * Initializes the default state of the component.
     */
    setDefaultState() {
      this.footer = document.querySelector('[data-js-footer]')
      this.referenceElement =
        document.querySelector('[data-js-product-box]') ||
        document.querySelector('[js-add-to-cart]')

      const htmlNodes = [this.footer, this.referenceElement].filter(
        (el) => el !== null
      )

      this.elementsToObserve.push(...htmlNodes)
      this.observer = new IntersectionObserver(this.handleIntersection, {
        threshold: 0.1,
      })
    }

    /**
     * Handles intersection events from the observer.
     *
     * @param {IntersectionObserverEntry[]} entries - List of intersection events.
     */
    handleIntersection(entries) {
      let isSticky = true

      entries.forEach((entry) => {
        /**
         * - Not sticky if we're above the reference element (either Product
         * Info or AddToCart button)
         * - Not sticky if any observed element is visible
         */
        if (
          window.scrollY <= (this.referenceElement?.offsetTop || 0) ||
          entry.isIntersecting
        ) {
          isSticky = false
        }
      })

      isSticky
        ? this.classList.add('is-sticky')
        : this.classList.remove('is-sticky')
    }

    /**
     * Sets up IntersectionObserver for the specified elements.
     */
    setupObservers() {
      if (!this.elementsToObserve.length) {
        console.error('StickyCartBar: No elements to observe.')

        return
      }

      this.elementsToObserve.forEach((element) => {
        this.observer?.observe(element)
      })
    }

    /**
     * Disconnects the IntersectionObserver.
     */
    disconnectObservers() {
      if (!this.observer || !this.elementsToObserve.length) {
        return
      }

      this.elementsToObserve.forEach((element) => {
        this.observer.unobserve(element)
      })

      this.observer.disconnect()
      this.observer = null
    }
  }

  customElements.define('sticky-cart-bar', StickyCartBar)
})()
